import React, { useState, useEffect, useCallback, useRef, memo } from 'react';
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  Pressable,
  StyleSheet,
  Modal,
  ScrollView,
  KeyboardAvoidingView,
  Platform,
} from 'react-native';
import type { TextInput as TextInputType } from 'react-native';
import { ADMIN_COLORS } from '@/constants/admin/colors';
import { SVG_COMPONENTS } from '@/assets/admin';
import { CORK_BOARD_INPUT_LIMITS } from '@/constants/residents/corkBoardConstants';
import { TagColorPicker } from './TagColorPicker';
import { TagIconPicker } from './TagIconPicker';
import type { CorkBoardTag } from '@/types/residents/corkBoard';

interface TagFormModalProps {
  visible: boolean;
  tag: CorkBoardTag | null;
  isSaving: boolean;
  onSave: (data: { name: string; color: string | null; icon: string | null }) => void;
  onClose: () => void;
}

export const TagFormModal: React.FC<TagFormModalProps> = memo(
  ({ visible, tag, isSaving, onSave, onClose }) => {
    const isEditing = !!tag;
    const [name, setName] = useState('');
    const [color, setColor] = useState<string | null>(null);
    const [icon, setIcon] = useState<string | null>(null);
    const [nameError, setNameError] = useState('');
    const nameInputRef = useRef<TextInputType>(null);

    useEffect(() => {
      if (visible) {
        setName(tag?.name ?? '');
        setColor(tag?.color ?? null);
        setIcon(tag?.icon ?? null);
        setNameError('');
        const timer = setTimeout(() => nameInputRef.current?.focus(), 100);
        return () => clearTimeout(timer);
      }
    }, [visible, tag]);

    const validate = useCallback((): boolean => {
      const trimmed = name.trim();
      if (!trimmed) {
        setNameError('Tag name is required');
        return false;
      }
      if (trimmed.length > CORK_BOARD_INPUT_LIMITS.tagNameMax) {
        setNameError(`Name must be ${CORK_BOARD_INPUT_LIMITS.tagNameMax} characters or fewer`);
        return false;
      }
      setNameError('');
      return true;
    }, [name]);

    const handleSave = useCallback(() => {
      if (isSaving || !validate()) return;
      onSave({
        name: name.trim(),
        color: color ?? null,
        icon: icon ?? null,
      });
    }, [isSaving, validate, onSave, name, color, icon]);

    const handleOverlayPress = useCallback(() => {
      if (!isSaving) onClose();
    }, [isSaving, onClose]);

    return (
      <Modal visible={visible} transparent animationType="fade" onRequestClose={isSaving ? undefined : onClose}>
        <KeyboardAvoidingView
          style={styles.overlay}
          behavior={Platform.OS === 'ios' ? 'padding' : undefined}
        >
          <Pressable style={styles.overlayDismiss} onPress={handleOverlayPress}>
            <Pressable style={styles.modalContainer} onPress={(e) => e.stopPropagation()}>
              {/* Header */}
              <View style={styles.header}>
                <Text style={styles.headerTitle}>{isEditing ? 'Edit Tag' : 'New Tag'}</Text>
                <TouchableOpacity
                  onPress={onClose}
                  disabled={isSaving}
                  hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
                >
                  <View style={isSaving ? styles.closeButtonDisabled : undefined}>
                    <SVG_COMPONENTS.CANCEL_X width={18} height={18} color={ADMIN_COLORS.TEXT.SECONDARY} />
                  </View>
                </TouchableOpacity>
              </View>

              <ScrollView style={styles.body} contentContainerStyle={styles.bodyContent} keyboardShouldPersistTaps="handled">
                {/* Name */}
                <View style={styles.field}>
                  <Text style={styles.label}>Name *</Text>
                  <TextInput
                    ref={nameInputRef}
                    style={[styles.input, nameError ? styles.inputError : null]}
                    value={name}
                    onChangeText={(text) => {
                      setName(text);
                      if (nameError) setNameError('');
                    }}
                    placeholder="e.g. Events, For Sale, Help Needed"
                    placeholderTextColor={ADMIN_COLORS.TEXT_TERTIARY}
                    maxLength={CORK_BOARD_INPUT_LIMITS.tagNameMax}
                  />
                  {nameError ? <Text style={styles.errorText}>{nameError}</Text> : null}
                </View>

                {/* Color Picker */}
                <TagColorPicker selectedColor={color} onSelectColor={setColor} />

                {/* Icon Picker */}
                <TagIconPicker selectedIcon={icon} onSelectIcon={setIcon} />

                {/* Preview */}
                {(name.trim() || color || icon) && (
                  <View style={styles.previewSection}>
                    <Text style={styles.label}>Preview</Text>
                    <View style={styles.previewRow}>
                      <View style={[styles.previewBadge, { backgroundColor: color ? hexToRgba(color, 0.15) : '#374151' }]}>
                        {icon ? <Text style={styles.previewIcon}>{icon}</Text> : null}
                        <Text style={[styles.previewText, color ? { color } : null]}>
                          {name.trim() || 'Tag Name'}
                        </Text>
                      </View>
                    </View>
                  </View>
                )}
              </ScrollView>

              {/* Footer */}
              <View style={styles.footer}>
                <TouchableOpacity style={styles.cancelButton} onPress={onClose} disabled={isSaving}>
                  <Text style={styles.cancelButtonText}>Cancel</Text>
                </TouchableOpacity>
                <TouchableOpacity
                  style={[styles.saveButton, isSaving && styles.saveButtonDisabled]}
                  onPress={handleSave}
                  disabled={isSaving}
                >
                  <Text style={styles.saveButtonText}>{isSaving ? 'Saving...' : 'Save'}</Text>
                </TouchableOpacity>
              </View>
            </Pressable>
          </Pressable>
        </KeyboardAvoidingView>
      </Modal>
    );
  },
);

TagFormModal.displayName = 'TagFormModal';

function hexToRgba(hex: string, alpha: number): string {
  const cleaned = hex.replace('#', '');
  const r = parseInt(cleaned.substring(0, 2), 16);
  const g = parseInt(cleaned.substring(2, 4), 16);
  const b = parseInt(cleaned.substring(4, 6), 16);
  if (isNaN(r) || isNaN(g) || isNaN(b)) return `rgba(107, 114, 128, ${alpha})`;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.6)',
  },
  overlayDismiss: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  modalContainer: {
    width: '90%',
    maxWidth: 480,
    maxHeight: '85%',
    backgroundColor: ADMIN_COLORS.BACKGROUND.PRIMARY,
    borderRadius: 12,
    overflow: 'hidden',
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 20,
    paddingVertical: 16,
    borderBottomWidth: 1,
    borderBottomColor: ADMIN_COLORS.BORDER,
  },
  headerTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: ADMIN_COLORS.TEXT.PRIMARY,
  },
  closeButtonDisabled: {
    opacity: 0.3,
  },
  body: {
    flex: 1,
  },
  bodyContent: {
    padding: 20,
    gap: 20,
  },
  field: {
    gap: 6,
  },
  label: {
    fontSize: 13,
    fontWeight: '500',
    color: ADMIN_COLORS.TEXT.SECONDARY,
  },
  input: {
    backgroundColor: ADMIN_COLORS.INPUT.PRIMARY,
    borderWidth: 1,
    borderColor: ADMIN_COLORS.INPUT.BORDER,
    borderRadius: 8,
    paddingHorizontal: 14,
    paddingVertical: 10,
    fontSize: 14,
    color: ADMIN_COLORS.TEXT.PRIMARY,
  },
  inputError: {
    borderColor: ADMIN_COLORS.ERROR,
  },
  errorText: {
    fontSize: 12,
    color: ADMIN_COLORS.ERROR,
  },
  previewSection: {
    gap: 8,
    paddingTop: 4,
  },
  previewRow: {
    flexDirection: 'row',
  },
  previewBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 100,
    gap: 5,
  },
  previewIcon: {
    fontSize: 14,
  },
  previewText: {
    fontSize: 13,
    fontWeight: '500',
    color: ADMIN_COLORS.TEXT.PRIMARY,
  },
  footer: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    gap: 12,
    paddingHorizontal: 20,
    paddingVertical: 16,
    borderTopWidth: 1,
    borderTopColor: ADMIN_COLORS.BORDER,
  },
  cancelButton: {
    paddingHorizontal: 20,
    paddingVertical: 10,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: ADMIN_COLORS.BORDER_SECONDARY,
  },
  cancelButtonText: {
    fontSize: 14,
    fontWeight: '500',
    color: ADMIN_COLORS.TEXT.SECONDARY,
  },
  saveButton: {
    paddingHorizontal: 20,
    paddingVertical: 10,
    borderRadius: 8,
    backgroundColor: ADMIN_COLORS.BUTTON.PRIMARY_BACKGROUND,
  },
  saveButtonDisabled: {
    opacity: 0.5,
  },
  saveButtonText: {
    fontSize: 14,
    fontWeight: '600',
    color: ADMIN_COLORS.BUTTON.PRIMARY_TEXT,
  },
});
